import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import TextField from "@components/ui/textfields/TextField";
import Button from "@components/ui/buttons/Button";
import Icon1 from "@assets/icons/Icon";
import { invitationsApi } from "@services/api/invitations";
import { raiseError } from "context/error.service";
import { GoogleIcon, AppleIcon } from "@components/ui/icons/ProviderIcons";
import { showNotification } from "context/notification.service";
import firebase from "@services/firebase";
import {
  signInWithCustomToken,
  signInWithEmailAndPassword,
  linkWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { useAuthStore } from "@store/auth";

export default function AcceptInvitationScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const code = searchParams.get("code");
  const user = useAuthStore((s) => s.user);
  const hasRedirected = useRef(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  // Invitation details
  const [orgName, setOrgName] = useState("");
  const [orgLogoUrl, setOrgLogoUrl] = useState("");
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [customToken, setCustomToken] = useState("");
  const [invitationValid, setInvitationValid] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Validate invitation on mount
  useEffect(() => {
    const validateInvitation = async () => {
      if (!token && !code) {
        setValidationError("Lien d'invitation invalide");
        setIsValidating(false);
        return;
      }

      try {
        const response = await invitationsApi.validate({
          token: token || undefined,
          code: code || undefined
        });

        if (response.valid) {
          setInvitationValid(true);
          setOrgName(response.org_name || "");
          setOrgLogoUrl(response.org_logo_url || "");
          setInviteeEmail(response.invitee_email || "");
          setCustomToken(response.custom_token || "");
        } else {
          setValidationError(response.error_message || "Invitation invalide");
        }
      } catch (error) {
        console.error("Failed to validate invitation:", error);
        setValidationError("Impossible de valider l'invitation");
      } finally {
        setIsValidating(false);
      }
    };

    validateInvitation();
  }, [token, code]);

  // Prevent automatic redirect to home if user is authenticated (from auth listener)
  // Only redirect when acceptance explicitly succeeds
  useEffect(() => {
    // If user gets authenticated but we're still on this page and haven't redirected,
    // it means the custom token sign-in happened but acceptance might have failed
    // Don't redirect automatically - let handleAcceptInvitation handle it
    if (user && !hasRedirected.current) {
      // Do nothing - stay on invitation page
      console.log("User authenticated via custom token, waiting for acceptance...");
    }
  }, [user]);

  const handleAcceptInvitation = async () => {
    // Validation
    if (!password) {
      raiseError({
        type: "Champ requis",
        reason: "Veuillez entrer un mot de passe",
        solutions: ["Choisissez un mot de passe pour votre compte"],
      });
      return;
    }

    if (password !== confirmPassword) {
      raiseError({
        type: "Mots de passe différents",
        reason: "Les mots de passe ne correspondent pas",
        solutions: ["Assurez-vous que les deux mots de passe sont identiques"],
      });
      return;
    }

    if (password.length < 6) {
      raiseError({
        type: "Mot de passe trop court",
        reason: "Le mot de passe doit contenir au moins 6 caractères",
        solutions: ["Choisissez un mot de passe plus long et sécurisé"],
      });
      return;
    }

    if (!customToken) {
      raiseError({
        type: "Erreur",
        reason: "Token d'authentification manquant",
        solutions: ["Veuillez rafraîchir la page et réessayer"],
      });
      return;
    }

    setIsLoading(true);
    try {
      // 1. First, accept the invitation with password (backend will update Firebase Auth)
      // We need to sign in with custom token temporarily for the API call to work
      await signInWithCustomToken(firebase.auth, customToken);

      // 2. Call accept endpoint - this sets the password in Firebase Auth
      await invitationsApi.accept({
        token: token || undefined,
        code: code || undefined,
        password
      });

      // 3. Sign out from custom token (success - invitation accepted)
      await firebase.auth.signOut();

      // 4. Sign in with email and password to get a proper auth token
      await signInWithEmailAndPassword(firebase.auth, inviteeEmail, password);

      // Only if everything succeeds, show success and navigate
      showNotification({
        type: "success",
        message: `Bienvenue dans ${orgName} !`,
      });

      // Mark that we're redirecting on success
      hasRedirected.current = true;

      // Navigate to home - user is now authenticated with their new account
      navigate("/home");
    } catch (error: any) {
      console.error("Failed to accept invitation:", error);

      // Sign out the user since acceptance failed - stay on invitation page
      try {
        await firebase.auth.signOut();
      } catch (signoutError) {
        console.error("Failed to sign out after error:", signoutError);
      }

      // Map error messages to French
      let errorMessage = "Impossible de créer votre compte";
      const backendError = error.response?.data?.detail || "";

      if (backendError.includes("password") || backendError.includes("weak")) {
        errorMessage = "Le mot de passe est trop faible ou invalide";
      } else if (backendError.includes("expired") || backendError.includes("expir")) {
        errorMessage = "L'invitation a expiré";
      } else if (backendError.includes("already") || backendError.includes("exists")) {
        errorMessage = "Ce compte existe déjà";
      } else if (backendError.includes("invalid") || backendError.includes("not found")) {
        errorMessage = "Invitation invalide ou introuvable";
      }

      raiseError({
        type: "Erreur d'inscription",
        reason: errorMessage,
        solutions: [
          "Vérifiez que le mot de passe respecte les exigences (minimum 6 caractères)",
          "Réessayez dans quelques instants",
        ],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthAccept = async (provider: GoogleAuthProvider | OAuthProvider) => {
    if (!customToken) {
      raiseError({
        type: "Erreur",
        reason: "Token d'authentification manquant",
        solutions: ["Veuillez rafraîchir la page et réessayer"],
      });
      return;
    }

    setIsLoading(true);
    try {
      // 1. Sign in with custom token (as the pending user)
      await signInWithCustomToken(firebase.auth, customToken);

      // 2. Link OAuth provider to the pending user account
      await linkWithPopup(firebase.auth.currentUser!, provider);

      // 3. Accept invitation (no password needed)
      await invitationsApi.accept({
        token: token || undefined,
        code: code || undefined,
      });

      // 4. Force token refresh to pick up new custom claims
      await firebase.auth.currentUser?.getIdToken(true);

      showNotification({
        type: "success",
        message: `Bienvenue dans ${orgName} !`,
      });

      hasRedirected.current = true;
      navigate("/home");
    } catch (error: any) {
      console.error("OAuth acceptance failed:", error);
      try {
        await firebase.auth.signOut();
      } catch (_) {}

      // Map error messages to French
      let errorMessage = "Impossible de créer votre compte";

      if (error.code === "auth/credential-already-in-use") {
        errorMessage = "Ce compte Google/Apple est déjà utilisé par un autre compte. Utilisez la méthode email/mot de passe.";
      } else if (error.code === "auth/popup-closed-by-user") {
        errorMessage = "La fenêtre de connexion a été fermée. Veuillez réessayer.";
      } else if (error.code === "auth/cancelled-popup-request") {
        errorMessage = "La demande de connexion a été annulée.";
      } else if (error.response?.data?.detail) {
        const backendError = error.response.data.detail;
        if (backendError.includes("expired") || backendError.includes("expir")) {
          errorMessage = "L'invitation a expiré";
        } else if (backendError.includes("already") || backendError.includes("exists")) {
          errorMessage = "Ce compte existe déjà";
        } else if (backendError.includes("invalid") || backendError.includes("not found")) {
          errorMessage = "Invitation invalide ou introuvable";
        }
      }

      raiseError({
        type: "Erreur d'inscription",
        reason: errorMessage,
        solutions: ["Réessayez ou utilisez la méthode email/mot de passe"],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAccept = () =>
    handleOAuthAccept(new GoogleAuthProvider());

  const handleAppleAccept = () => {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    return handleOAuthAccept(provider);
  };

  // Loading state
  if (isValidating) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center px-6">
        <div className="text-h2 text-neutral-400">Validation de l'invitation...</div>
      </div>
    );
  }

  // Invalid invitation
  if (!invitationValid) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center px-6">
        <div className="flex flex-col items-center gap-4 max-w-400 w-full">
          <Icon1 name="erreur" size={60} color="#EF4444" />
          <div className="text-hx2 font-bold text-center">Invitation invalide</div>
          <div className="text-h3 text-neutral-400 text-center">{validationError}</div>
          <Button
            variant="large"
            label="Retour à la connexion"
            onClick={() => navigate("/login")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen flex flex-row">
      <div className="flex w-full flex-col items-center justify-center px-6 py-10 min-h-screen">
        <form
          className="max-w-400 w-full flex flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            handleAcceptInvitation();
          }}
        >
          <div className="items-center flex flex-col gap-1">
            {/* Organization Logo */}
            <div className="border p-4 rounded-full flex flex-col justify-center items-center mb-4">
              {orgLogoUrl ? (
                <img
                  src={orgLogoUrl}
                  alt={orgName}
                  className="w-16 h-16 object-contain"
                />
              ) : (
                <Icon1 name="organisation" size={50} color="lightgrey" />
              )}
            </div>

            <div className="text-hx3 font-bold">Rejoindre {orgName}</div>
            <div className="text-hx1 mb-4 text-neutral-400 text-center">
              Créez votre mot de passe pour accepter l'invitation
            </div>

            <TextField
              type="email"
              label="Email"
              value={inviteeEmail}
              disabled={true}
              className="mb-2"
            />

            <TextField
              type="password"
              label="Mot de passe"
              placeholder="******"
              value={password}
              onChange={(value) => setPassword(value)}
              className="mb-2"
              disabled={isLoading}
            />

            <TextField
              type="password"
              label="Confirmer le mot de passe"
              placeholder="******"
              value={confirmPassword}
              onChange={(value) => setConfirmPassword(value)}
              className="mb-8"
              disabled={isLoading}
            />

            <Button
              variant="large"
              disabled={isLoading}
              type="submit"
              label={isLoading ? "Création du compte..." : "Accepter l'invitation"}
              onClick={() => {}}
            />

            {/* Divider */}
            <div className="flex items-center gap-3 w-full my-4">
              <div className="flex-1 h-px bg-neutral-200" />
              <span className="text-h3 text-neutral-400">ou</span>
              <div className="flex-1 h-px bg-neutral-200" />
            </div>

            {/* Google */}
            <button
              type="button"
              disabled={isLoading}
              onClick={handleGoogleAccept}
              className="w-full flex items-center justify-center gap-3 border border-neutral-200 rounded-xl py-3 px-4 hover:bg-neutral-50 transition-colors disabled:opacity-50"
            >
              <GoogleIcon size={20} />
              <span className="text-h2 text-neutral-700">Continuer avec Google</span>
            </button>

            {/* Apple */}
            <button
              type="button"
              disabled={isLoading}
              onClick={handleAppleAccept}
              className="w-full flex items-center justify-center gap-3 border border-neutral-200 rounded-xl py-3 px-4 hover:bg-neutral-50 transition-colors disabled:opacity-50 mt-2"
            >
              <AppleIcon size={20} />
              <span className="text-h2 text-neutral-700">Continuer avec Apple</span>
            </button>
          </div>
        </form>

        <div className="mt-8 text-h3 text-neutral-400">© 2026 HexaFlow</div>
      </div>

      {/* Right side panel with org branding */}
      <div className="p-2 lg:flex hidden w-full max-w-[50%]">
        <div
          style={{ borderRadius: 60 }}
          className="bg-black h-full w-full relative flex flex-col justify-between"
        >
          <div className="absolute w-full items-center justify-center flex flex-col h-full">
            {orgLogoUrl ? (
              <img
                style={{ borderRadius: 60 }}
                className="opacity-100 max-w-500 h-full w-full object-contain"
                src={orgLogoUrl}
                alt={orgName}
              />
            ) : (
              <img
                style={{ borderRadius: 60 }}
                className="opacity-100 max-w-500 h-full w-full object-contain"
                src="https://i.pinimg.com/736x/5a/c7/02/5ac702ae36c9fd20f45061a194e2b006.jpg"
                alt="Welcome"
              />
            )}
          </div>

          <div className="flex flex-col p-10 z-10">
            <div className="font-bold text-white text-hx3">{orgName}</div>
            <div className="text-hx1 mt-1 text-neutral-200">
              Vous avez été invité à rejoindre cette organisation
            </div>
          </div>

          <div className="flex flex-row items-start w-full max-w-600 justify-start p-10 gap-10 z-10">
            <div className="flex flex-col">
              <div className="text-h1 text-white">Bienvenue</div>
              <div className="text-h2 mt-1 text-neutral-300">
                Créez votre mot de passe pour commencer à collaborer
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
